import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    LOCALE_STORAGE_KEY,
    SupportedLocale,
    isSupportedLocale,
    FALLBACK_LOCALE,
} from '../i18n/config';
import * as constants from '../constants/constants';
import { axiosCaller } from '../utils/apiCaller';

interface UseLocaleResult {
    locale: SupportedLocale;
    activeLocales: SupportedLocale[];
    setLocale: (next: SupportedLocale, options?: { persist?: boolean }) => Promise<void>;
}

export function useLocale(): UseLocaleResult {
    const dispatch = useDispatch();

    const locale = useSelector((s: any): SupportedLocale => {
        const raw = s?.app?.locale;
        return isSupportedLocale(raw) ? (raw as SupportedLocale) : FALLBACK_LOCALE;
    });

    const activeLocales = useSelector((s: any): SupportedLocale[] => {
        const profile = s?.app?.companyProfile;
        const raw = profile?.activeLocales;
        if (Array.isArray(raw) && raw.length) {
            return raw.filter(isSupportedLocale) as SupportedLocale[];
        }
        return ['sq', 'en'];
    });

    const setLocale = useCallback(
        async (next: SupportedLocale, options?: { persist?: boolean }): Promise<void> => {
            if (!isSupportedLocale(next)) return;

            // Apply locally first so UI updates without waiting on the network round-trip.
            try { localStorage.setItem(LOCALE_STORAGE_KEY, next); } catch { /* ignore */ }
            dispatch({ type: constants.SET_LOCALE, payload: next });

            const shouldPersist = options?.persist !== false;
            if (!shouldPersist) return;

            const token = localStorage.getItem('mikaBearerToken');
            if (!token) return; // not logged in — localStorage-only preference

            try {
                await axiosCaller('PATCH', 'user/locale', { locale: next });
            } catch {
                // Non-fatal: local preference still applies. A failed persist just means
                // next login may revert to company default until this endpoint succeeds.
            }
        },
        [dispatch],
    );

    return { locale, activeLocales, setLocale };
}
